import { create } from 'zustand';

export type ToastType = 'info' | 'success' | 'error';

export interface ToastItem {
  id: string;
  message: string;
  type: ToastType;
}

export interface ShowToastOptions {
  message: string;
  type?: ToastType;
  duration?: number;
}

interface ToastState {
  toasts: ToastItem[];
  show: (options: ShowToastOptions) => string;
  dismiss: (id: string) => void;
}

const DEFAULT_DURATION = 4000;
const timers = new Map<string, ReturnType<typeof setTimeout>>();

function createToastId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],

  show: ({ message, type = 'info', duration = DEFAULT_DURATION }) => {
    const id = createToastId();
    const toast: ToastItem = { id, message, type };

    set((state) => ({ toasts: [...state.toasts, toast] }));

    const existing = timers.get(id);
    if (existing) clearTimeout(existing);

    const timer = setTimeout(() => {
      get().dismiss(id);
      timers.delete(id);
    }, duration);

    timers.set(id, timer);
    return id;
  },

  dismiss: (id) => {
    const timer = timers.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.delete(id);
    }

    set((state) => ({ toasts: state.toasts.filter((toast) => toast.id !== id) }));
  },
}));

export const toastStore = {
  show: (options: ShowToastOptions) => useToastStore.getState().show(options),
  dismiss: (id: string) => useToastStore.getState().dismiss(id),
};
